import { Component, inject, OnInit } from '@angular/core';
import { DialogRef } from '@ngneat/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogType, NotificationType } from '@core/enums';

/**
 * The `ConfirmationDialogComponent` class is responsible for displaying a confirmation dialog with customizable title, message, buttons, and type.
 * The dialog can be used to confirm an action, such as deleting an item, leaving a page, or joining a group.
 * The dialog has two buttons: Yes and No. The Yes button is used to confirm the action, and the No button is used to cancel the action.
 *
 * @enum {ConfirmationDialogType} - The `ConfirmationDialogType` enum is used to specify the type of confirmation dialog in `data` config object.\
 * It has the following values:
 * - `DELETE`: Used to confirm the deletion of an item.
 * - `LEAVE`: Used to confirm leaving a page.
 * - `JOIN`: Used to confirm joining a group.
 * - `FORM_LEAVE`: Used to confirm leaving a form with unsaved changes.
 * - `Accept`: Used to confirm accepting an item.
 * - `Deny`: Used to confirm denying an item.
 * - `APPROVE`: Used to confirm approving an item.
 * - `REJECT`: Used to confirm rejecting an item.
 * - `SHIFTS`: Used to confirm deleting all shifts for an item.
 * - `MOVE_TO_TRASH`: Used to confirm moving an item to the trash.
 * - `PERMANENTLY_DELETE`: Used to confirm permanently deleting an item.
 * - `SIMPLE_MESSAGE`: Used to display a simple message.
 *
 * @Property {Object} data - The `data` property is an object that holds the configuration for the confirmation dialog component.
 * @description With the data property, the dialog can be customized with the following properties:
 * - `title`: The title of the dialog.
 * - `message`: The message to display in the dialog.
 * - `emphasize`: The text to emphasize in the message.
 * - `postEmphasizeMessage`: The message to display after the emphasized text.
 * - `confirmButtonText`: The text to display on the confirm button.
 * - `cancelButtonText`: The text to display on the cancel button.
 * - `messageHTML`: The message to display in HTML format.
 * - `category`: The category of the item to confirm the action on.
 * - `type`: The type of confirmation dialog.
 *
 */
@Component({
  selector: 'app-confirmation-dialog',
  templateUrl: './confirmation-dialog.component.html',
  styleUrls: ['./confirmation-dialog.component.scss'],
  standalone: false,
})
export class ConfirmationDialogComponent implements OnInit {
  /* The `data` property is an object that holds the configuration for the confirmation dialog component.
It has the following properties: */
  data: {
    title: string;
    message: string;
    emphasize: string;
    postEmphasizeMessage?: string;
    confirmButtonText: string;
    cancelButtonText: string;
    messageHTML?: string;
    category: NotificationType;
    type: ConfirmationDialogType;
  };
  message: string;
  private readonly _dialogService = inject(DialogRef);
  private readonly _translateService = inject(TranslateService);

  constructor() {
    this.data = this._dialogService.data || {};
  }

  get isSimpleMessage() {
    return this.data.type === ConfirmationDialogType.SIMPLE_MESSAGE;
  }

  ngOnInit() {
    this._initDetails();
  }

  closeDialog(yes = false) {
    this._dialogService.close(yes);
  }

  /**
   * The function `_initDetails()` initializes the details of a confirmation dialog based on the type of
   * dialog specified.
   */
  private _initDetails() {
    this.data.confirmButtonText = this.data?.confirmButtonText || this._translateService.instant('Yes');
    this.data.cancelButtonText = this.data?.cancelButtonText || this._translateService.instant('No');
    const category = this.data?.category ? this.data?.category.charAt(0).toUpperCase() + this.data?.category.slice(1) : '';
    const appendCategory = category ? ' ' + category : '';

    switch (this.data?.type) {
      case ConfirmationDialogType.DELETE:
        this.data.title = this.data?.title || this._translateService.instant('Delete Confirmation');
        this.message = this._translateService.instant('Are you sure you want to delete');
        break;
      case ConfirmationDialogType.LEAVE:
        this.data.title = this.data?.title || this._translateService.instant('Leave Confirmation');
        this.message = this._translateService.instant('Are you sure you want to leave');
        break;
      case ConfirmationDialogType.JOIN:
        this.data.title = this.data?.title || this._translateService.instant('Join Confirmation');
        this.message = this._translateService.instant('Are you sure you want to join');
        break;
      case ConfirmationDialogType.APPROVE_JOIN:
        this.data.title = this.data?.title || this._translateService.instant('Approve Join Confirmation');
        this.message = this._translateService.instant('Are you sure you want to approve this join request');
        break;
      case ConfirmationDialogType.FORM_LEAVE:
        this.data.title = this.data?.title || this._translateService.instant('Leave Confirmation');
        this.message = this._translateService.instant('You have unsaved changes. Are you sure you want to leave');
        break;
      case ConfirmationDialogType.Accept:
        this.data.title = this.data?.title || this._translateService.instant('Accept Confirmation');
        this.message = this._translateService.instant('Are you sure you want to accept this' + appendCategory);
        break;
      case ConfirmationDialogType.Deny:
        this.data.title = this.data?.title || this._translateService.instant('Deny Confirmation');
        this.message = this._translateService.instant('Are you sure you want to deny this' + appendCategory);
        break;
      case ConfirmationDialogType.APPROVE:
        this.data.title = this.data?.title || this._translateService.instant('Approve Confirmation');
        this.message = this._translateService.instant('Are you sure you want to approve this' + appendCategory);
        break;
      case ConfirmationDialogType.REJECT:
        this.data.title = this.data?.title || this._translateService.instant('Reject Confirmation');
        this.message = this._translateService.instant('Are you sure you want to reject this' + appendCategory);
        break;
      case ConfirmationDialogType.UNAPPROVE:
        this.data.title = this.data?.title || this._translateService.instant('Reject Confirmation');
        this.message = this._translateService.instant('Are you sure you want to unapprove this' + appendCategory);
        break;
      case ConfirmationDialogType.SHIFTS:
        this.data.title = this.data?.title || this._translateService.instant('Shifts Delete Confirmation');
        this.message = this._translateService.instant('Are you sure you want to delete all shifts for') + ' ';
        break;
      case ConfirmationDialogType.MOVE_TO_TRASH:
        this.data.title = this.data?.title || this._translateService.instant('Move to Trash Confirmation');
        this.message = this._translateService.instant('Are you sure you want to move this to trash');
        break;
      case ConfirmationDialogType.PERMANENTLY_DELETE:
        this.data.title = this.data?.title || this._translateService.instant('Delete Confirmation');
        this.message = this._translateService.instant('Are you sure you want to permanently delete this');
        break;
      case ConfirmationDialogType.DELETE_IMAGE:
        this.data.title = this.data?.title || this._translateService.instant('Delete Confirmation');
        this.message = this._translateService.instant('Are you sure you want to delete this image');
        break;
      case ConfirmationDialogType.DELETE_DOCUMENT:
        this.data.title = this.data?.title || this._translateService.instant('Delete Confirmation');
        this.message = this._translateService.instant('Are you sure you want to delete this document');
        break;
      case ConfirmationDialogType.SIMPLE_MESSAGE:
        this.data.title = this.data?.title || this._translateService.instant('Message');
        this.message = this.data?.message || '';
        this.data.cancelButtonText = this._translateService.instant('Close');
        break;

      default:
        this.data.title = this.data?.title || this._translateService.instant('Confirmation');
        this.message = this.data?.message || this._translateService.instant('Are you sure you want to continue');
        break;
    }
  }
}
